import axios from 'axios';
import { toast } from 'react-toastify';

const BASE_URL = process.env.REACT_APP_API_URL || '/api';

const api = axios.create({
  baseURL: BASE_URL,
  timeout: 30000,
  headers: { 'Content-Type': 'application/json' }
});

// Request interceptor - attach token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('accessToken');
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor - handle token refresh
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      try {
        const refreshToken = localStorage.getItem('refreshToken');
        const accessToken  = localStorage.getItem('accessToken');

        if (!refreshToken) {
          logout();
          return new Promise(() => {});   // silent — page is redirecting
        }

        const response = await axios.post(`${BASE_URL}/auth/refresh-token`, {
          AccessToken:  accessToken,
          RefreshToken: refreshToken
        });

        if (response.data.Success) {
          const { AccessToken, RefreshToken } = response.data.Data;
          localStorage.setItem('accessToken',  AccessToken);
          localStorage.setItem('refreshToken', RefreshToken);
          originalRequest.headers.Authorization = `Bearer ${AccessToken}`;
          return api(originalRequest);
        }

        // Refresh succeeded at HTTP level but API said not-OK
        logout();
        return new Promise(() => {});     // silent — page is redirecting
      } catch {
        logout();
        return new Promise(() => {});     // silent — page is redirecting
      }
    }

    const message = error.response?.data?.Message || error.message || 'Something went wrong';
    if (error.response?.status !== 401 && !error.config?._quiet) {
      toast.error(message);
    }

    return Promise.reject(error);
  }
);

const logout = () => {
  localStorage.clear();
  window.location.href = '/login';
};

export default api;

// Auth
export const authAPI = {
  login:          (data) => api.post('/auth/login', data),
  logout:         () => api.post('/auth/logout'),
  forgotPassword: (data) => api.post('/auth/forgot-password', data),
  resetPassword:  (data) => api.post('/auth/reset-password', data),
  changePassword: (data) => api.post('/auth/change-password', data),
  refreshToken:   (data) => api.post('/auth/refresh-token', data),
  getMyPermissions: () => api.get('/auth/my-permissions', { _quiet: true }),
};

// Dashboard
export const dashboardAPI = {
  getKPIs:           (params) => api.get('/dashboard/kpis', { params }),
  getLeadsByStatus:  (params) => api.get('/dashboard/leads-by-status', { params }),
  getLeadsBySource:  (params) => api.get('/dashboard/leads-by-source', { params }),
  getLeadsByLocation:(params) => api.get('/dashboard/leads-by-location', { params }),
  getMonthlyTrend:   (params) => api.get('/dashboard/monthly-trend', { params }),
  getTeamPerformance:(params) => api.get('/dashboard/team-performance', { params: params || {} }),
};

// Users
export const usersAPI = {
  getAll:           (params) => api.get('/users', { params }),
  getById:          (id) => api.get(`/users/${id}`),
  getProfile:       () => api.get('/users/profile'),
  create:           (data) => api.post('/users', data),
  update:           (id, data) => api.put(`/users/${id}`, data),
  delete:           (id) => api.delete(`/users/${id}`),
  manageTags:       (id, data) => api.put(`/users/${id}/tags`, data),
  uploadPicture:    (id, formData) => api.post(`/users/${id}/profile-picture`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  adminResetPassword: (id, data) => api.post(`/users/${id}/reset-password`, data),
  getRelationshipManagers: () => api.get('/users/relationship-managers', { _quiet: true }),
};

// Branches
export const branchAPI = {
  getAll:           (params) => api.get('/branches', { params }),
  getById:          (id) => api.get(`/branches/${id}`),
  create:           (data) => api.post('/branches', data),
  update:           (id, data) => api.put(`/branches/${id}`, data),
  delete:           (id) => api.delete(`/branches/${id}`),
};

// Events
export const eventsAPI = {
  getAll:           (params) => api.get('/events', { params }),
  getById:          (id) => api.get(`/events/${id}`),
  create:           (data) => api.post('/events', data),
  update:           (id, data) => api.put(`/events/${id}`, data),
  delete:           (id) => api.delete(`/events/${id}`),
  getAttendees:     (id) => api.get(`/events/${id}/attendees`),
  addAttendee:      (id, data) => api.post(`/events/${id}/attendees`, data),
  updateAttendee:   (attendeeId, data) => api.put(`/events/attendees/${attendeeId}`, data),
  removeAttendee:   (attendeeId) => api.delete(`/events/attendees/${attendeeId}`),
};

// Campaign Rules (marketing automation)
export const campaignRulesAPI = {
  getAll:   (params) => api.get('/campaign-rules', { params }),
  getById:  (id) => api.get(`/campaign-rules/${id}`),
  create:   (data) => api.post('/campaign-rules', data),
  update:   (id, data) => api.put(`/campaign-rules/${id}`, data),
  delete:   (id) => api.delete(`/campaign-rules/${id}`),
  runNow:   (id) => api.post(`/campaign-rules/${id}/run`),
};

// Referral Program
export const referralProgramAPI = {
  getCodes:         (params) => api.get('/referralprogram/codes', { params }),
  saveCode:         (data) => api.post('/referralprogram/codes', data),
  getPayouts:       (params) => api.get('/referralprogram/payouts', { params }),
  addPayout:        (data) => api.post('/referralprogram/payouts', data),
  markPayoutPaid:   (payoutId) => api.put(`/referralprogram/payouts/${payoutId}/paid`),
};

// Leads
export const leadsAPI = {
  getAll:           (params) => api.get('/leads', { params, _quiet: true }),
  getTags:          () => api.get('/leads/tags', { _quiet: true }),
  getById:          (id) => api.get(`/leads/${id}`),
  getTimeline:      (id) => api.get(`/leads/${id}/timeline`),
  create:           (data) => api.post('/leads', data),
  update:           (id, data) => api.put(`/leads/${id}`, data),
  assign:           (id, data) => api.post(`/leads/${id}/assign`, data),
  bulkAssign:       (data) => api.post('/leads/bulk-assign', data),
  convert:          (id) => api.post(`/leads/${id}/convert`),
  delete:           (id) => api.delete(`/leads/${id}`),
  getMatches:       (id, page = 1, pageSize = 20) => api.get(`/leads/${id}/matches`, { params: { page, pageSize } }),
};

// Customers
export const customersAPI = {
  getAll:           (params) => api.get('/customers', { params }),
  getById:          (id) => api.get(`/customers/${id}`),
  manageTags:       (id, data) => api.put(`/customers/${id}/tags`, data),
  getStats:         (params) => api.get('/customers/stats', { params }),
};

// Followups
export const followupsAPI = {
  getAll:    (params) => api.get('/followups', { params }),
  getToday:  () => api.get('/followups/today'),
  getMissed: () => api.get('/followups/missed'),
  save:      (data) => api.post('/followups', data),
  delete:    (id) => api.delete(`/followups/${id}`),
  getStats:  (params) => api.get('/followups/stats', { params }),
};

// Communications
export const communicationsAPI = {
  saveCall:    (data, recording) => {
    const formData = new FormData();
    Object.entries(data).forEach(([k, v]) => v !== undefined && v !== null && formData.append(k, v));
    if (recording) formData.append('recording', recording);
    return api.post('/communications/call', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  saveWhatsApp:(data, screenshot) => {
    const formData = new FormData();
    Object.entries(data).forEach(([k, v]) => v !== undefined && v !== null && formData.append(k, v));
    if (screenshot) formData.append('screenshot', screenshot);
    return api.post('/communications/whatsapp', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  uploadCallRecording: (callId, file) => {
    const fd = new FormData();
    fd.append('recording', file);
    return api.post(`/communications/call/${callId}/recording`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  saveEmail:   (data) => api.post('/communications/email', data),
  saveSMS:     (data) => api.post('/communications/sms', data),
  saveMeeting: (data) => api.post('/communications/meeting', data),
  getCenter:   (type, id) => api.get(`/communications/${type}/${id}`),
};

// Documents
export const documentsAPI = {
  upload:    (formData) => api.post('/documents', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  getAll:    (params) => api.get('/documents', { params }),
  delete:    (id) => api.delete(`/documents/${id}`),
  download:  (id) => api.get(`/documents/${id}/download`, { responseType: 'blob' }),
};

// #854: Call Logs + Recordings, surfaced on Lead View
export const deviceCallLogAPI = {
  getAll: (params) => api.get('/DeviceCallLog', { params }),
};

export const callRecordingAPI = {
  getAll:  (params)   => api.get('/CallRecording', { params }),
  upload:  (formData) => api.post('/CallRecording/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  getSasToken: (blobName) => api.get('/CallRecording/sas-token', { params: { blobName } }),
};

// Notifications
export const notificationsAPI = {
  getAll:       (params) => api.get('/notifications', { params }),
  markRead:     (id) => api.patch(`/notifications/${id}/read`),
  markAllRead:  () => api.patch('/notifications/read-all'),
};

// Activities
export const activitiesAPI = {
  getAll: (params) => api.get('/activities', { params }),
  getStats: (params) => api.get('/activities/stats', { params }),
  getTypes: () => api.get('/activities/types', { _quiet: true }),
};

// Admin-managed Matrimony content: feedback, contact messages, FAQs, support settings
export const adminContentAPI = {
  getFeedback: (params) => api.get('/admincontent/feedback', { params }),
  getApiLogs:  (params) => api.get('/admincontent/api-logs', { params, _quiet: true }),
  getFraudWatch: () => api.get('/admincontent/fraud-watch', { _quiet: true }),
  getContact:  (params) => api.get('/admincontent/contact', { params }),
  updateContactStatus: (id, data) => api.put(`/admincontent/contact/${id}/status`, data),
  updateFeedbackStatus: (id, data) => api.put(`/admincontent/feedback/${id}/status`, data),
  getOpenTicketCount: () => api.get('/admincontent/open-ticket-count', { _quiet: true }),
  getFaqs:     () => api.get('/admincontent/faqs'),
  saveFaq:     (data) => api.post('/admincontent/faq', data),
  deleteFaq:   (id) => api.delete(`/admincontent/faq/${id}`),
  getSupport:  () => api.get('/admincontent/settings/support'),
  saveSupport: (data) => api.put('/admincontent/settings/support', data),
  getStories:  (params) => api.get('/admincontent/success-stories', { params }),
  saveStory:   (data) => api.post('/admincontent/success-story', data),
  deleteStory: (id) => api.delete(`/admincontent/success-story/${id}`),
};

// Roles
export const rolesAPI = {
  getAll:           () => api.get('/roles'),
  getPermissions:   (id) => api.get(`/roles/${id}/permissions`),
  savePermissions:  (id, data) => api.put(`/roles/${id}/permissions`, data),
};

// Reports
export const reportsAPI = {
  exportLeads:   (params) => api.get('/reports/leads/export', { params, responseType: 'blob' }),
  exportUsers:   (params) => api.get('/reports/users/export', { params, responseType: 'blob' }),
  getLeadReport: (params) => api.get('/reports/leads', { params }),
};

// Daily Progress Report -- Calls / Profiles Created / Onboarded Customers
// for one calendar date (defaults server-side to today).
export const dailyProgressReportAPI = {
  get: (date) => api.get('/daily-progress-report', { params: date ? { date } : {} }),
};

// Settings
export const settingsAPI = {
  getDepartments:  () => api.get('/settings/departments'),
  getDesignations: (deptId) => api.get('/settings/designations', { params: { deptId } }),
  getLeadSources:  () => api.get('/settings/lead-sources', { _quiet: true }),
  // Lead config CRUD (statuses / priorities / sources)
  getLeadConfig:    (type) => api.get(`/settings/lead-${type}`, { _quiet: true }),
  saveLeadConfig:   (type, data) => api.post(`/settings/lead-${type}`, data),
  toggleLeadConfig: (type, id, isActive) => api.patch(`/settings/lead-${type}/${id}/toggle`, { IsActive: isActive }),
};

// Masterdata
export const masterdataAPI = {
  getAll:    (key)          => api.get(`/masterdata/${key}`,          { _quiet: true }),
  getParents:(key)          => api.get(`/masterdata/${key}/parents`,  { _quiet: true }),
  create:    (key, data)    => api.post(`/masterdata/${key}`,         data),
  update:    (key, id, data)=> api.put(`/masterdata/${key}/${id}`,    data),
  toggle:    (key, id)      => api.delete(`/masterdata/${key}/${id}`),
};

// Matrimony Profiles
export const matrimonyAPI = {
  getDashboard:   ()           => api.get('/matrimonyprofiles/dashboard',           { _quiet: true }),
  getAll:         (params)     => api.get('/matrimonyprofiles',                     { params, _quiet: true }),
  getById:        (id)         => api.get(`/matrimonyprofiles/${id}`,               { _quiet: true }),
  getMasterData:  ()           => api.get('/matrimonyprofiles/masterdata',          { _quiet: true }),
  getRMPerformance: ()         => api.get('/matrimonyprofiles/rm-performance',      { _quiet: true }),
  update:         (id, data)   => api.put(`/matrimonyprofiles/${id}`, data),
  getViews:       (id)         => api.get(`/matrimonyprofiles/${id}/views`,         { _quiet: true }),
  getContactViewCount: (id)    => api.get(`/matrimonyprofiles/${id}/contact-view-count`, { _quiet: true }),
  getContactViewers:   (id)    => api.get(`/matrimonyprofiles/${id}/contact-viewers`,    { _quiet: true }),
  getAccessLog:   (id)         => api.get(`/matrimonyprofiles/${id}/access-log`,        { _quiet: true }),
  getZeroAccessLogProfiles: () => api.get('/matrimonyprofiles/access-log/zero',         { _quiet: true }),
  getActivityLog: (id, page, size) => api.get(`/matrimonyprofiles/${id}/activity-log`,  { params: { page, size }, _quiet: true }),
  getAdminActions: (id, page, size) => api.get(`/matrimonyprofiles/${id}/admin-actions`, { params: { page, size }, _quiet: true }),
  getProfilePayments: (id)    => api.get(`/matrimonyprofiles/${id}/payments`,           { _quiet: true }),
  getMostActive:  (params)     => api.get('/matrimonyprofiles/most-active',         { params, _quiet: true }),
  getReferralReport: (params)  => api.get('/matrimonyprofiles/referral-report',     { params, _quiet: true }),
  getInterests:   (id, type)   => api.get(`/matrimonyprofiles/${id}/interests`,     { params: { type }, _quiet: true }),
  create:         (data)       => api.post('/matrimonyprofiles', data),
  getPhotos:      (id)         => api.get(`/matrimonyprofiles/${id}/photos`,        { _quiet: true }),
  uploadPhoto:    (id, file, typeId = 2)   => {
    const form = new FormData();
    form.append('file', file);
    form.append('typeId', typeId);
    return api.post(`/matrimonyprofiles/${id}/photos`, form, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  deletePhoto:    (id, photoId) => api.delete(`/matrimonyprofiles/${id}/photos/${photoId}`),
  deleteProfile:  (id, data) => api.delete(`/matrimonyprofiles/${id}`, { data }),
  restoreProfile: (id) => api.put(`/matrimonyprofiles/${id}/restore`),
  pauseProfile:      (id) => api.put(`/matrimonyprofiles/${id}/pause`),
  reactivateProfile: (id) => api.put(`/matrimonyprofiles/${id}/reactivate`),
  verifyProfile:  (id, idTypes) => api.post(`/matrimonyprofiles/${id}/verify`, { IDTypes: idTypes }),
  updateContact:  (id, data) => api.put(`/matrimonyprofiles/${id}/contact`, data),
  getIDProofs:    (id) => api.get(`/matrimonyprofiles/${id}/id-proofs`, { _quiet: true }),
  uploadIDProofDocument: (id, formData) => api.post(`/matrimonyprofiles/${id}/id-proof-documents`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  getIDProofDocuments: (id) => api.get(`/matrimonyprofiles/${id}/id-proof-documents`, { _quiet: true }),
  getIDVerificationQueue: (params) => api.get('/matrimonyprofiles/id-verification-queue', { params }),
  reviewIDProofDocument: (documentId, data) => api.post(`/matrimonyprofiles/id-proof-documents/${documentId}/review`, data),
  addNote:        (id, noteText) => api.post(`/matrimonyprofiles/${id}/notes`, { NoteText: noteText }),
  getNotes:       (id) => api.get(`/matrimonyprofiles/${id}/notes`, { _quiet: true }),
  assignRM:       (id, rmUserID) => api.post(`/matrimonyprofiles/${id}/assign-rm`, { RMUserID: rmUserID }),
  issueCoupon:    (id, discountPercent) => api.post(`/matrimonyprofiles/${id}/coupon`, { DiscountPercent: discountPercent }),
  getRM:          (id) => api.get(`/matrimonyprofiles/${id}/rm`, { _quiet: true }),
  removeRM:       (id, rmUserID) => api.delete(`/matrimonyprofiles/${id}/rm/${rmUserID}`),
  updatePartnerPreference: (id, json) => api.put(`/matrimonyprofiles/${id}/partner-preferences`, { json }),
  updateHoroscopeChart: (id, json) => api.put(`/matrimonyprofiles/${id}/horoscope-chart`, { json }),
  getMatches: (id, page = 1, pageSize = 20) => api.get(`/matrimonyprofiles/${id}/matches`, { params: { page, pageSize } }),
  updateLanguage: (id, languageCode) => api.put(`/matrimonyprofiles/${id}/language`, { LanguageCode: languageCode }),
};

// Matrimony Plans
export const matrimonyPlansAPI = {
  getAll:   (params) => api.get('/matrimonyplans',       { params, _quiet: true }),
  getById:  (id)     => api.get(`/matrimonyplans/${id}`, { _quiet: true }),
  create:   (data)   => api.post('/matrimonyplans',       data),
  update:   (id, data) => api.put(`/matrimonyplans/${id}`, data),
  delete:          (id)          => api.delete(`/matrimonyplans/${id}`),
  assignToProfile: (profileId, data) => api.post(`/matrimonyplans/assign/${profileId}`, data),
  getFeatures:    (planId)         => api.get(`/matrimonyplans/${planId}/features`, { _quiet: true }),
  addFeature:     (planId, data)   => api.post(`/matrimonyplans/${planId}/features`, data),
  updateFeature:  (featureId, data) => api.put(`/matrimonyplans/features/${featureId}`, data),
  deleteFeature:  (featureId)      => api.delete(`/matrimonyplans/features/${featureId}`),
};

// Photo Approval
export const photoApprovalAPI = {
  getPending:  (params) => api.get('/photoapproval/pending', { params, _quiet: true }),
  approve:     (id)     => api.post(`/photoapproval/${id}/approve`),
  reject:      (id, reason) => api.post(`/photoapproval/${id}/reject`, { reason }),
  bulkApprove: (photoIds)         => api.post('/photoapproval/bulk-approve', { photoIds }),
  bulkReject:  (photoIds, reason) => api.post('/photoapproval/bulk-reject', { photoIds, reason }),
};

// Reported Profiles (#267)
export const reportedProfilesAPI = {
  getPending: (params) => api.get('/reportedprofiles/pending', { params, _quiet: true }),
  dismiss:    (id)     => api.post(`/reportedprofiles/${id}/dismiss`),
};

// Delete Profile Requests
export const deleteProfileRequestsAPI = {
  getAll: (params) => api.get('/delete-profile-requests', { params, _quiet: true }),
  updateStatus: (id, status) => api.put(`/delete-profile-requests/${id}/status`, { status }),
};

export const paymentOrdersAPI = {
  getAll: (params) => api.get('/payment-orders', { params, _quiet: true }),
  verify: (id) => api.post(`/payment-orders/${id}/verify`),
  createPaymentLink: (profileId, planId) => api.post('/payment-orders/payment-link', { ProfileID: profileId, PlanID: planId }),
  validatePaymentLink: (id) => api.post(`/payment-orders/${id}/validate-link`),
  getRevenueDashboard: (params) => api.get('/payment-orders/revenue-dashboard', { params, _quiet: true }),
};

export const refundRequestsAPI = {
  getAll:  (params) => api.get('/refund-requests', { params, _quiet: true }),
  getById: (id)     => api.get(`/refund-requests/${id}`, { _quiet: true }),
  create:  (paymentOrderId, reason) => api.post('/refund-requests', { PaymentOrderID: paymentOrderId, CancellationReason: reason }),
  submit:  (id, notes) => api.post(`/refund-requests/${id}/submit`, { Notes: notes }),
  approve: (id, notes) => api.post(`/refund-requests/${id}/approve`, { Notes: notes }),
  reject:  (id, notes) => api.post(`/refund-requests/${id}/reject`, { Notes: notes }),
};

export const incentivesAPI = {
  getMonthly: (year, month) => api.get('/incentives/monthly', { params: { year, month }, _quiet: true }),
  markPaid: (userId, year, month) => api.post('/incentives/mark-paid', { UserID: userId, Year: year, Month: month }),
};

// Matrimony Messages (admin view — read-only)
export const matrimonyMessagesAPI = {
  getAll:        (params)                => api.get('/matrimonymessages', { params, _quiet: true }),
  getForProfile: (profileId)             => api.get(`/matrimonymessages/profile/${profileId}`, { _quiet: true }),
  getThread:     (profileId1, profileId2) =>
    api.get('/matrimonymessages/thread', { params: { profileId1, profileId2 }, _quiet: true }),
  // #990: chat-request state (Pending/Accepted/Rejected) for a profile.
  getChatRequests: (profileId) => api.get(`/matrimonymessages/chat-requests/${profileId}`, { _quiet: true }),
};

// Lead ↔ Profile Link
export const leadProfileLinkAPI = {
  getProfileSummary: (leadId, profileId) =>
    api.get(`/leadprofilelink/lead/${leadId}/profile-summary`, { params: { profileId }, _quiet: true }),
  getLeadSummary: (profileId, leadId) =>
    api.get(`/leadprofilelink/profile/${profileId}/lead-summary`, { params: { leadId }, _quiet: true }),
  link:   (leadId, profileId) => api.post('/leadprofilelink/link', { LeadID: leadId, ProfileID: profileId }),
  unlink: (leadId)            => api.delete(`/leadprofilelink/lead/${leadId}`),
  getLinkedLeadIds: () => api.get('/leadprofilelink/linked-lead-ids', { _quiet: true }),
};

// Daily Call Queue
export const dailyQueueAPI = {
  getQueue:   (params) => api.get('/dailyqueue',         { params, _quiet: true }),
  getSummary: (params) => api.get('/dailyqueue/summary', { params, _quiet: true }),
  logCall:    (data)   => api.post('/dailyqueue/log-call', data),
};

// Communication DB APIs
export const communicationAPI = {
  getWhatsAppLog: (params) => api.get('/communication/whatsapp-log', { params, _quiet: true }),
  getEmailLog:    (params) => api.get('/communication/email-log',    { params, _quiet: true }),
};

// WhatsApp Business API
export const whatsAppAPI = {
  getConfig:      ()           => api.get('/whatsapp/config',    { _quiet: true }),
  getTemplates:   ()           => api.get('/whatsapp/templates', { _quiet: true }),
  createTemplate: (data)       => api.post('/whatsapp/templates', data),
  deleteTemplate: (name)       => api.delete(`/whatsapp/templates/${encodeURIComponent(name)}`),
  send:           (data)       => api.post('/whatsapp/send', data),
  bulkSend:       (data)       => api.post('/whatsapp/bulk-send', data),
  getLogs:        (params)     => api.get('/whatsapp/logs', { params, _quiet: true }),
  getBulkJobs:    (params)     => api.get('/whatsapp/bulk-jobs', { params, _quiet: true }),
  uploadMedia:    (file)       => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post('/whatsapp/upload-media', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
};
